/**
 * Chummys reviews — <chummys-reviews>
 *
 * The rows are pure CSS marquees; this only decides when they run. They start
 * on arrival (which also fires the reveals and the bars), stop when the section
 * scrolls away so nothing animates off-screen, and each row gets its own
 * duration and a negative delay so they never line up. If a row is shorter
 * than the viewport the loop would show its seam, so the cards are cloned
 * until each track is comfortably wider than the row.
 */
(() => {
  if (customElements.get('chummys-reviews')) return;
  const mqReduced = window.matchMedia('(prefers-reduced-motion: reduce)');

  class ChummysReviews extends HTMLElement {
    connectedCallback() {
      const base = Math.max(20, parseFloat(this.dataset.speed) || 90);
      this.querySelectorAll('.chummys-reviews__row').forEach((row, i) => {
        row.style.setProperty('--dur', `${Math.round(base * (1 + i * 0.22))}s`);
        row.style.setProperty('--delay', `${-Math.round(base * (0.15 + i * 0.31))}s`);
      });
      if (!mqReduced.matches) this.fill();
      this.io = new IntersectionObserver((entries) => {
        for (const e of entries) {
          if (e.isIntersecting) { this.classList.add('is-in', 'is-running'); }
          else this.classList.remove('is-running');
        }
      }, { threshold: 0.05 });
      this.io.observe(this);
      this.onResize = () => { if (!mqReduced.matches) this.fill(); };
      window.addEventListener('resize', this.onResize, { passive: true });

      // A phone has no hover, so the CSS pause never fires there: hold the row
      // still while a finger is on it, and let it go again on release.
      this.onDown = (e) => { const row = e.target.closest('.chummys-reviews__row'); if (row) row.classList.add('is-held'); };
      this.onUp = () => this.querySelectorAll('.is-held').forEach((r) => r.classList.remove('is-held'));
      this.addEventListener('pointerdown', this.onDown, { passive: true });
      window.addEventListener('pointerup', this.onUp, { passive: true });
      window.addEventListener('pointercancel', this.onUp, { passive: true });
    }

    disconnectedCallback() {
      if (this.io) this.io.disconnect();
      window.removeEventListener('resize', this.onResize);
      this.removeEventListener('pointerdown', this.onDown);
      window.removeEventListener('pointerup', this.onUp);
      window.removeEventListener('pointercancel', this.onUp);
    }

    fill() {
      this.querySelectorAll('.chummys-reviews__row').forEach((row) => {
        const tracks = row.querySelectorAll('.chummys-reviews__track');
        const first = tracks[0];
        if (!first || !first.children.length) return;
        const originals = first.dataset.count ? Number(first.dataset.count) : first.children.length;
        first.dataset.count = originals;
        const need = row.clientWidth * 1.15;
        let guard = 0;
        while (first.scrollWidth < need && guard++ < 6) {
          for (let i = 0; i < originals; i++) {
            const c = first.children[i].cloneNode(true);
            c.setAttribute('aria-hidden', 'true');
            first.appendChild(c);
          }
        }
        // the second track must be a copy of the first, whatever it grew to
        for (let t = 1; t < tracks.length; t++) tracks[t].innerHTML = first.innerHTML;
      });
    }
  }

  customElements.define('chummys-reviews', ChummysReviews);
})();
